import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import cogoToast from "cogo-toast";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
} from "@material-ui/core";
import CustomButton from "../../../../components/CustomButton";
import CustomInput from "../../../../components/CustomInput";
import CustomSwitch from "../../../../components/CustomSwitch";
import CustomTextArea from "../../../../components/CustomTextArea";
import icons from "../../../../assets/icons/icon-collection.svg";
import { deleteAccount } from "../../../../redux/actions/settingsActions";
import { ERROR } from "../../../../redux/actionTypes";
import {
  getProfileAction,
  updateProfileAction,
} from "../../../../redux/actions/profileActions";
import contactImportService from "../../../../services/contactImportService";
import httpRequest from "../../../../ajax";

const Account = () => {
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const dispatch = useDispatch();
  const [reason, setReason] = useState("");
  const [state, setState] = useState({
    checkedB: true,
  });
  const [autoInviteEnabled, setAutoInviteEnabled] = useState(true);
  const [leaderboardOptIn, setLeaderboardOptIn] = useState(false);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [deleteContactsOpen, setDeleteContactsOpen] = useState(false);
  const [deletingContacts, setDeletingContacts] = useState(false);

  const { profile } = useSelector((store) => store);
  const { profileData } = profile;

  useEffect(() => {
    getProfileAction(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (profileData?.auto_invite_enabled !== undefined) {
      setAutoInviteEnabled(profileData.auto_invite_enabled == 1 ? true : false);
    }
  }, [profileData]);

  useEffect(() => {
    contactImportService
      .getStats()
      .then((res) => {
        const data = res?.data ?? res;
        setImportedCount(data?.imported || 0);
      })
      .catch(() => {});
  }, []);

  // Fetch leaderboard opt-in status
  useEffect(() => {
    httpRequest("get", "/leaderboard")
      .then((res) => {
        if (res?.data?.status) {
          setLeaderboardOptIn(res.data.data.user_opted_in || false);
        }
      })
      .catch(() => {});
  }, []);

  const handleLeaderboardOptIn = (newValue) => {
    setLeaderboardLoading(true);
    setLeaderboardOptIn(newValue);
    httpRequest("put", "/leaderboard/opt-in", { opt_in: newValue })
      .then((res) => {
        if (res?.data?.status) {
          cogoToast.success(
            newValue
              ? "Your real name will now show on the leaderboard"
              : "You are now anonymized on the leaderboard",
            { position: "top-center", hideAfter: 3 }
          );
        }
      })
      .catch(() => {
        setLeaderboardOptIn(!newValue);
        cogoToast.error("Failed to update leaderboard preference", {
          position: "top-center", hideAfter: 3,
        });
      })
      .finally(() => setLeaderboardLoading(false));
  };

  const handleChange = (event) => {
    setState({ ...state, [event.target.name]: event.target.checked });
  };

  const handleDeleteContacts = async () => {
    setDeletingContacts(true);
    try {
      await contactImportService.deleteAllContacts();
      setImportedCount(0);
      setDeleteContactsOpen(false);
      cogoToast.success(
        "All imported contacts deleted. Consent has been withdrawn."
      );
    } catch (e) {
      cogoToast.error(
        e?.response?.data?.message || "Failed to delete contacts"
      );
    } finally {
      setDeletingContacts(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const result = await deleteAccount({ reason });
      if (result?.type === ERROR) {
        cogoToast.error(
          result.payload || "Failed to delete account. Please try again.",
        );
        setDeleting(false);
      }
    } catch {
      cogoToast.error("Failed to delete account. Please try again.");
      setDeleting(false);
    }
  };

  const handleCloseDialog = () => {
    if (!deleting) setOpen(false);
  };

  const gender = {
    1: "Male",
    2: "Female",
    3: "Others",
  };

  return (
    <div>
      <h6 className="bolder-text">Personal Information</h6>
      <p className="small-text co-darkgray mt-3">
        Kindly provide your personal information. Note that this information
        will not be visible publicly unless you choose to make it public.{" "}
      </p>

      <form className="mt-4">
        <CustomInput
          type="text"
          label="Email Address"
          id="role"
          value={profileData.email}
          isDisabled
        />
        <CustomInput
          type="text"
          label="Phone"
          id="name"
          value={profileData.phone}
          isDisabled
        />
        <CustomInput
          type="text"
          label="Gender"
          id="role"
          value={gender[profileData.gender]}
          isDisabled
        />
        <CustomInput
          type="text"
          label="Birthday"
          id="name"
          value={profileData.dob}
          isDisabled
        />
        {/* <div className="my-4 d-flex align-items-center justify-content-between">
          Turn Off All Notifications
          <FormControlLabel
            control={
              <CustomSwitch
                checked={state.checkedB}
                onChange={handleChange}
                name="checkedB"
              />
            }
          />
        </div> */}
        <div className="my-4 d-flex align-items-center justify-content-between">
          Auto-invite Ideal Talents
          <FormControlLabel
            control={
              <CustomSwitch
                checked={autoInviteEnabled}
                onChange={() => {
                  const newValue = !autoInviteEnabled;
                  setAutoInviteEnabled(newValue);
                  updateProfileAction(
                    { auto_invite_enabled: newValue ? 1 : 0 },
                    dispatch,
                  );
                }}
                name="autoInviteEnabled"
              />
            }
          />
        </div>

        {/* ── Leaderboard Opt-In Toggle (PRD §7.2) ─────────────── */}
        <div className="my-4 d-flex align-items-center justify-content-between">
          <div>
            <div>Show Real Name on Leaderboard</div>
            <div className="small-text co-darkgray mt-1">
              Default: anonymized as "Referrer_XXXX". Toggle to show your real name on the referrer leaderboard.
            </div>
          </div>
          <FormControlLabel
            control={
              <CustomSwitch
                checked={leaderboardOptIn}
                onChange={() => handleLeaderboardOptIn(!leaderboardOptIn)}
                name="leaderboardOptIn"
                disabled={leaderboardLoading}
              />
            }
          />
        </div>

        <h6 className="bolder-text mt-5">Imported Contacts</h6>
        <p className="small-text co-darkgray mt-2">
          You have {importedCount} imported contact
          {importedCount === 1 ? "" : "s"} stored for matching and invites.
          Opting out deletes all imported contacts, related invite records, and
          stored Google access. You will be asked for consent again before the
          next import. You can also do this from the Contacts tab.
        </p>
        <div className="d-flex mt-3 justify-content-end">
          <span className="w-50">
            <CustomButton
              handleClick={() => setDeleteContactsOpen(true)}
              text="Opt out & delete all contacts"
              backgroundColor="#fff"
              color="#E24329"
              borderColor="#E24329"
              disabled={importedCount === 0}
            />
          </span>
        </div>

        <div className="d-flex mt-5 justify-content-end">
          <span className="w-25">
            <CustomButton
              handleClick={() => setOpen(true)}
              text="Delete my account"
              backgroundColor="#E24329"
              color="#fff"
            />
          </span>
        </div>
        <Dialog
          open={deleteContactsOpen}
          onClose={() => !deletingContacts && setDeleteContactsOpen(false)}
          aria-labelledby="confirm-delete-contacts"
          PaperProps={{
            style: {
              width: "35rem",
              borderRadius: "1rem",
              padding: "0.75rem",
            },
          }}
        >
          <DialogTitle>Opt out and delete contacts</DialogTitle>
          <DialogContent>
            <strong className="co-lightred">
              Delete all {importedCount} imported contacts and withdraw consent?
              This cannot be undone.
            </strong>
          </DialogContent>
          <DialogActions>
            <CustomButton
              handleClick={() => setDeleteContactsOpen(false)}
              text="Cancel"
              backgroundColor="#fff"
              color="#64748B"
              borderColor="#CBD5E1"
              disabled={deletingContacts}
              additionalClass="mr-2"
            />
            <CustomButton
              handleClick={handleDeleteContacts}
              text={deletingContacts ? "Deleting..." : "Delete"}
              backgroundColor="#E24329"
              color="#fff"
              disabled={deletingContacts}
            />
          </DialogActions>
        </Dialog>
        <Dialog
          open={open}
          onClose={handleCloseDialog}
          aria-labelledby="confirm-delete"
          PaperProps={{
            style: {
              width: "35rem",
              borderRadius: "1rem",
              padding: "0.75rem",
            },
          }}
        >
          <div className="d-flex justify-content-between align-items-centter">
            <DialogTitle>Delete Account</DialogTitle>{" "}
            <div className="close-button">
              <IconButton onClick={handleCloseDialog} disabled={deleting}>
                <svg width="30" height="30" className="rounded-circle">
                  <use href={`${icons}#close`}></use>
                </svg>
              </IconButton>
            </div>
          </div>
          <DialogContent>
            <strong className="co-lightred">
              Are you sure you want to delete your account?
            </strong>
            <CustomTextArea
              placeholder="why do you want to delete your account?"
              label="Reason"
              onChange={(e) => setReason(e.target.value)}
              isDisabled={deleting}
            />
          </DialogContent>
          <DialogActions>
            <span className="w-50 mr-3">
              <CustomButton
                handleClick={handleDelete}
                text="Delete"
                backgroundColor="#E24329"
                color="#fff"
                loading={deleting}
                disabled={deleting}
              />
            </span>
          </DialogActions>
        </Dialog>
      </form>
    </div>
  );
};

export default Account;
